import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as itinerariesApi from './itineraries.api';

import type {
  InstalmentDateChangeProposeRequest,
  InstalmentSplitProposeRequest,
  RepriceApprovalApproveRequest,
  RepriceApprovalListItem,
  RepriceApprovalListQuery,
  RepriceApprovalRejectRequest,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

/**
 * React Query hooks for the dedicated itinerary-reprice approval queue
 * (Task 7). Mirrors `use-lead-approvals.ts` - the list is reviewer-scoped
 * server-side (ADMIN sees all, SALES_LEAD sees rows assigned to them,
 * SALES_AGENT sees only their own submissions, read-only), so the query key
 * carries the full request query rather than a client-side scope.
 */

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const REPRICE_APPROVALS_QK = {
  /** Prefix matching every reprice-approval query - list + any future detail. */
  all: () => ['itineraries', 'reprice-approvals'] as const,
  list: (query: RepriceApprovalListQuery) => ['itineraries', 'reprice-approvals', query] as const,
  pendingCount: () => ['itineraries', 'reprice-approvals', 'pending-count'] as const,
};

export const useRepriceApprovals = (query: RepriceApprovalListQuery) =>
  useQuery({
    queryKey: REPRICE_APPROVALS_QK.list(query),
    queryFn: () => itinerariesApi.listRepriceApprovals(query),
    staleTime: 15_000,
  });

/**
 * Pending queue depth. Mirrors `useLeadApprovalsPendingCount` - backs the
 * sidebar nav red count badge. Reviewer-scoped server-side (ADMIN all,
 * SALES_LEAD assigned, SALES_AGENT their own), so no client-side scope
 * argument is needed.
 */
export const useRepriceApprovalsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: REPRICE_APPROVALS_QK.pendingCount(),
    queryFn: () => itinerariesApi.getRepriceApprovalsPendingCount(),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

/**
 * Approve a pending reprice. Admin or the assigned SALES_LEAD only (server
 * enforced - a 403 here is a stale UI, not an expected path). Does NOT
 * publish - it just unblocks the publish gate so the seller can send the
 * repriced quote. Invalidates the queue + the affected itinerary's detail so
 * the Preview-tab lock lifts without a manual refresh.
 */
export const useApproveReprice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      remark,
    }: {
      id: string;
      remark?: RepriceApprovalApproveRequest['remark'];
    }): Promise<RepriceApprovalListItem> => itinerariesApi.approveRepriceApproval(id, { remark }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: REPRICE_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', row.itineraryId] });
      toast.success('Reprice approved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to approve reprice'),
  });
};

/**
 * Reject a pending reprice. Admin or the assigned SALES_LEAD only. A remark
 * is required (enforced by the shared schema on the caller's form). Rejecting
 * CLEARS the reprice server-side - the solved discount/upsell is zeroed and
 * the itinerary returns to its pre-reprice quote, so the itinerary detail
 * invalidation here also drops the Preview-tab lock.
 */
export const useRejectReprice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      remark,
    }: {
      id: string;
      remark: RepriceApprovalRejectRequest['remark'];
    }): Promise<RepriceApprovalListItem> => itinerariesApi.rejectRepriceApproval(id, { remark }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: REPRICE_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', row.itineraryId] });
      toast.success('Reprice rejected');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reject reprice'),
  });
};

/**
 * Propose lowering the first instalment (kind INSTALMENT_SPLIT). Raises a
 * reprice approval; the plan + customer links only change once it is approved (an
 * ADMIN proposer auto-approves, so the plan refetch matters there). Invalidates
 * the reprice queue, the itinerary detail and its payment plan.
 */
export const useProposeInstalmentSplit = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstalmentSplitProposeRequest): Promise<RepriceApprovalListItem> =>
      itinerariesApi.proposeInstalmentSplit(itineraryId, body),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: REPRICE_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', itineraryId] });
      void qc.invalidateQueries({ queryKey: ['itineraries', itineraryId, 'payment-plan'] });
      toast.success(
        row.status === 'APPROVED'
          ? 'First installment updated - payment links are regenerating'
          : 'First installment change sent for approval',
      );
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to change the first installment'),
  });
};

/**
 * Propose changing the due date of one or more unpaid instalments (kind
 * INSTALMENT_DATE_CHANGE). Works on a PREVIEW or LOCKED (won) plan; an ADMIN
 * proposer auto-approves. Invalidates the reprice queue, the itinerary detail and
 * its payment plan.
 */
export const useProposeInstalmentDateChange = (itineraryId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstalmentDateChangeProposeRequest): Promise<RepriceApprovalListItem> =>
      itinerariesApi.proposeInstalmentDateChange(itineraryId, body),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: REPRICE_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', itineraryId] });
      void qc.invalidateQueries({ queryKey: ['itineraries', itineraryId, 'payment-plan'] });
      toast.success(
        row.status === 'APPROVED'
          ? 'Installments updated'
          : 'Installment changes sent for approval',
      );
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to change the installment dates'),
  });
};
